import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { syncOfflineQueue } from '../services/syncService'
import { getUnresolvedConflicts, getSyncHistory, resolveConflict, SyncConflict, SyncHistoryEntry } from '../lib/db/indexed-db'

export interface UseSyncManagerResult {
  isOnline: boolean
  pendingCount: number
  syncStatus: string
  lastSyncAt: string | null
  syncError: string | null
  conflicts: SyncConflict[]
  history: SyncHistoryEntry[]
  triggerSync: () => void
  resolveConflictById: (id: string) => Promise<void>
  refreshConflicts: () => Promise<void>
}

export function useSyncManager(): UseSyncManagerResult {
  const dispatch = useAppDispatch()
  const { isOnline, pendingCount, syncStatus, lastSyncAt, syncError } = useAppSelector(
    (state) => state.offline,
  )
  const [conflicts, setConflicts] = useState<SyncConflict[]>([])
  const [history, setHistory] = useState<SyncHistoryEntry[]>([])

  const refreshConflicts = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([getUnresolvedConflicts(), getSyncHistory()])
      setConflicts(c)
      setHistory(h)
    } catch {
      // IDB may not be available in test environments
    }
  }, [])

  useEffect(() => {
    refreshConflicts()
  }, [refreshConflicts, syncStatus])

  const triggerSync = useCallback(() => {
    if (isOnline) {
      syncOfflineQueue(dispatch)
    }
  }, [isOnline, dispatch])

  const resolveConflictById = useCallback(
    async (id: string) => {
      await resolveConflict(id)
      await refreshConflicts()
    },
    [refreshConflicts],
  )

  return {
    isOnline,
    pendingCount,
    syncStatus,
    lastSyncAt,
    syncError,
    conflicts,
    history,
    triggerSync,
    resolveConflictById,
    refreshConflicts,
  }
}
